import {
  SAFETY_MEASURE_ITEMS,
  type DocumentSignatureSummary,
  type SignedDocumentSummary,
  type WorkOrderForm344,
} from '@railroute/contracts/core';

// Наряд, разложенный по позициям бланка (приложение 1 к Правилам № 344).
//
// ЗАЧЕМ. Инспектор при разборе смотрит наряд целиком и ищет в нём пункты 6, 7,
// 8, 9, 10. Список полей и общий перечень подписей на этот вопрос не отвечают:
// подпись нужно видеть ПОД ТОЙ позицией, которую она удостоверяет.
//
// Модуль ЧИСТЫЙ: ни сети, ни разметки. Раскладка по позициям — правило, и
// проверяться оно должно на входных данных.
//
// НОВЫХ ИСТОЧНИКОВ ПРАВДЫ НЕТ. Всё считается из того же ответа сервера,
// который собран из тех же строк, что уходят в телефон.

export interface SheetSignature {
  id: string;
  role: DocumentSignatureSummary['signerNormativeRole'];
  itemNumber: string | null;
  name: string;
  employeeNumber: string;
  /**
   * Подписант БЕЗ учётной записи (issue #471): должность и организация, от
   * имени которой он расписался в графе 8.1. `null` — подпись работника нашей
   * организации, и у неё есть табельный номер.
   *
   * Лист обязан различать эти два случая словами: пустые скобки после фамилии
   * читались бы как «табельный номер потерян», а у представителя чужой
   * организации его не бывает вовсе.
   */
  externalSigner: { position: string; organizationName: string } | null;
  signedAt: string;
  level: DocumentSignatureSummary['signatureLevel'];
  verification: DocumentSignatureSummary['verificationStatus'];
  unlock: DocumentSignatureSummary['unlockMethod'] | null;
  deviceThumbprint: string | null;
}

export type SheetPositionKey =
  | 'issue'
  | 'measures'
  | 'internalApproval'
  | 'externalApproval'
  | 'admission'
  | 'start'
  | 'briefing'
  | 'shifts'
  | 'closure';

export interface SheetPosition {
  key: SheetPositionKey;
  /// Номер и название позиции бланка — то, что инспектор ищет глазами.
  title: string;
  signatures: SheetSignature[];
  /// Почему в графе прочерк. `null` — графа этому наряду требуется, и прочерк
  /// ей не нужен.
  ///
  /// Пункт 5 Правил № 344 требует в незаполняемой графе ПРОЧЕРК: пустое место
  /// читается как потерянная строка, прочерк — как «не требовалось». Тот же
  /// довод, по которому незаполненная графа пункта 5 остаётся в перечне.
  dash: string | null;
}

/** Строка графы пункта 5. Незаполненная остаётся в перечне — с прочерком. */
export interface SheetMeasureRow {
  itemNumber: string;
  title: string;
  hint: string;
  /// `null` — графа не заполнена. В бланке в ней прочерк, и лист его рисует.
  content: string | null;
  executor: { name: string; employeeNumber: string } | null;
  signature: SheetSignature | null;
}

// ИСТОЧНИК НАЗВАНИЙ — сам бланк, приложение 1 к Правилам № 344, расшифровка
// в docs/regulatory/rules-work-permits-344-2020.md. Правка 19.09.2026 по
// сверке владельца: названия здесь были частью выдуманы, частью сдвинуты по
// номерам. Инспектор ищет позицию по номеру и названию, и чужое название под
// правильным номером хуже отсутствующего — оно утверждает, что в бланке
// написано не то, что в нём написано.
//
// Что было исправлено и почему это не косметика:
//   - пункта 14 в бланке НЕТ. Последний — 13, «Работа окончена», и сдача
//     наряда с приёмкой рабочего места входят в него;
//   - пункт 12 называется «Продление наряда-допуска»; приёмка смены — одна
//     из его граф, а не название пункта;
//   - пункт 11 — состав бригады с отметкой об инструктаже;
//   - пункты 9 и 10 в бланке выписаны предложениями целиком, и укорачивать
//     их до «к работе допускаю» и «к работе приступил» нельзя: «к работе
//     приступил» стоит вообще в пункте 11, у другой подписи;
//   - пункт 5 называется «Мероприятия для обеспечения безопасности работ»;
//   - пункт 7 — «Мероприятия выполнил(и)».
//
// Правка 25.09.2026 (issue #467) по сверке владельца с подлинным бланком:
// графа 8 и графа 8.1 — ДВЕ позиции, а не одна, и внешняя из них только
// вторая. Графу 8 «Согласовано» подписывает начальник смены (участка), где
// идут работы, — работник САМОЙ организации с учётной записью. Графа 8.1 —
// открытая строка без заданной должности, и в неё ложится представитель
// ВНЕШНЕЙ организации, обслуживающей коммуникации, при работах по пункту 8
// Правил (дороги, ЛЭП, газо- и теплопроводы, все земляные работы). До #467
// лист называл внешними обе графы, а внутреннее согласование держал блоком
// «сверх бланка» — то есть утверждал, что приложение 1 такой позиции не
// знает, хотя это и есть её графа 8.
//
// ADR-004 §3a и ADR-007 §11 пока говорят обратное — внутреннее согласование
// названо в них не предусмотренным нормой, а графы 8 и 8.1 отнесены к внешней
// организации. Расхождение с решением владельца названо в PR; правка ADR идёт
// отдельным PR с явного одобрения владельца.
const POSITION_TITLES: Record<SheetPositionKey, string> = {
  issue: 'Пункт 6. Наряд-допуск выдал',
  measures: 'Пункт 7. Мероприятия выполнил(и)',
  internalApproval: 'Пункт 8. Согласовано — начальник смены (участка)',
  externalApproval: 'Пункт 8.1. Согласовано — обслуживающая организация',
  admission:
    'Пункт 9. Мероприятия выполнены, безопасность обеспечена, производителя работ ознакомил и проинструктировал, допуск разрешаю — допускающий',
  start:
    'Пункт 10. С условиями работы ознакомлен и проинструктирован, подготовку проверил, рабочее место принял — производитель работ',
  briefing: 'Пункт 11. Состав бригады и отметка о прохождении инструктажа',
  shifts: 'Пункт 12. Продление наряда-допуска',
  closure: 'Пункт 13. Работа окончена',
};

/**
 * Позиции бланка, у которых своих подписей нет.
 *
 * Пункты 1 и 3 называют производителя работ и допускающего в ШАПКЕ, до
 * мероприятий — так стоит в бланке. Их подписи ниже, в пунктах 10 и 9; здесь
 * это именование, а не подписная строка.
 *
 * Названия держатся рядом с POSITION_TITLES намеренно: все слова бланка — в
 * одном месте, и сверять их с приложением 1 нужно в одном файле, а не по
 * разметке.
 */
export const FORM_HEADINGS = {
  producer: 'Пункт 1. Производитель работ',
  scope: 'Пункт 2. Допускается к выполнению',
  admitting: 'Пункт 3. Допускающий(ие) к работе',
  responsible: 'Пункт 4. Ответственный руководитель',
  measures: 'Пункт 5. Мероприятия для обеспечения безопасности работ',
} as const;

const KIND_BY_POSITION: Record<
  Exclude<SheetPositionKey, 'measures' | 'externalApproval' | 'issue'>,
  string[]
> = {
  // Графа 8: внутреннее согласование живёт СВОИМ подписанным документом
  // (issue #445) — в отличие от графы 8.1, чья подпись стоит под документом
  // выдачи. Поэтому графа 8 берётся по виду документа, а 8.1 — по роли
  // подписи.
  internalApproval: ['WORK_ORDER_APPROVAL'],
  admission: ['WORK_ORDER_ADMISSION'],
  start: ['WORK_ORDER_START'],
  // Issue #533: подписи проводившего (по каждому работнику, с проверкой
  // знаний) — в той же графе, что подписи получивших.
  briefing: ['TARGET_BRIEFING_CONDUCT', 'TARGET_BRIEFING_ACK', 'TARGET_BRIEFING_PROXY_ACK'],
  shifts: ['SHIFT_EXTENSION'],
  closure: ['CLOSURE_HANDOVER', 'CLOSURE_ACCEPT'],
};

function toSheetSignature(signature: DocumentSignatureSummary): SheetSignature {
  return {
    id: signature.id,
    role: signature.signerNormativeRole,
    itemNumber: signature.mitigationItemNumber ?? null,
    name: signature.signedByNameSnapshot,
    employeeNumber: signature.signedByEmployeeNumberSnapshot,
    externalSigner: signature.externalSigner ?? null,
    signedAt: signature.signedAt,
    level: signature.signatureLevel,
    verification: signature.verificationStatus,
    unlock: signature.unlockMethod ?? null,
    deviceThumbprint: signature.deviceThumbprint ?? null,
  };
}

const byTime = (a: SheetSignature, b: SheetSignature) => a.signedAt.localeCompare(b.signedAt);

function signaturesOf(documents: readonly SignedDocumentSummary[], kinds: readonly string[]): SheetSignature[] {
  return documents
    .filter((document) => kinds.includes(document.documentKind))
    .flatMap((document) => document.signatures ?? [])
    .map(toSheetSignature)
    .sort(byTime);
}

export function issueDocumentOf(form: WorkOrderForm344): SignedDocumentSummary | null {
  return form.documents.find((d) => d.documentKind === 'WORK_ORDER_ISSUE') ?? null;
}

/**
 * Почему в графе стоит прочерк.
 *
 * Прочерк ставится там, где графа ЭТОМУ наряду не требуется, — и у двух граф
 * согласования условия РАЗНЫЕ, потому что это два разных согласования:
 *
 *   - графа 8 — внутреннее разрешение на работы, и требует его настройка
 *     организации, снятая на наряд при выписке (issue #445);
 *   - графа 8.1 — согласование с внешней организацией по пункту 8 Правил, и
 *     требует его вид работ, подтверждённый выдающим.
 *
 * Разойдись эти два условия хоть однажды — лист поставил бы прочерк в графе,
 * которую наряд обязан был собрать, то есть назвал бы необязательным то, без
 * чего бригаду допускать нельзя.
 */
function dashOf(form: WorkOrderForm344, key: SheetPositionKey): string | null {
  if (key === 'internalApproval') {
    return form.operationalApproval.required
      ? null
      : 'согласование начальника смены (участка) этому наряду не требуется';
  }
  if (key === 'externalApproval') {
    return form.approval.required
      ? null
      : 'согласование с обслуживающей организацией этому наряду не требуется';
  }
  return null;
}

/**
 * Позиции бланка с подписями под каждой.
 *
 * Пункты 6, 7 и графа 8.1 живут на ОДНОМ документе выдачи и различаются ролью
 * подписи: наряд подписывает выдающий, мероприятия — их исполнители,
 * согласование с внешней организацией — её представитель. Разложить их по
 * ролям — и есть работа листа. Графа 8 стоит особняком: внутреннее
 * согласование подписывается своим документом (issue #445).
 */
export function sheetPositions(form: WorkOrderForm344): SheetPosition[] {
  const issue = issueDocumentOf(form);
  const issueSignatures = (issue?.signatures ?? []).map(toSheetSignature);
  const position = (key: SheetPositionKey, signatures: SheetSignature[]): SheetPosition => ({
    key,
    title: POSITION_TITLES[key],
    signatures: [...signatures].sort(byTime),
    dash: dashOf(form, key),
  });

  return [
    position('issue', issueSignatures.filter((s) => s.role === 'WORK_ORDER_ISSUER')),
    position('measures', issueSignatures.filter((s) => s.role === 'MITIGATION_EXECUTOR')),
    position('internalApproval', signaturesOf(form.documents, KIND_BY_POSITION.internalApproval)),
    position('externalApproval', issueSignatures.filter((s) => s.role === 'SAFETY_APPROVER')),
    position('admission', signaturesOf(form.documents, KIND_BY_POSITION.admission)),
    position('start', signaturesOf(form.documents, KIND_BY_POSITION.start)),
    position('briefing', signaturesOf(form.documents, KIND_BY_POSITION.briefing)),
    position('shifts', signaturesOf(form.documents, KIND_BY_POSITION.shifts)),
    position('closure', signaturesOf(form.documents, KIND_BY_POSITION.closure)),
  ];
}

/**
 * Все десять граф пункта 5, включая незаполненные.
 *
 * Незаполненная графа ОСТАЁТСЯ в перечне: по пункту 5 Правил в ней ставится
 * прочерк, и его отсутствие читается как потерянная строка — при разборе это
 * разница между «мероприятие не требовалось» и «страницу подменили».
 */
export function measureRows(form: WorkOrderForm344): SheetMeasureRow[] {
  const issue = issueDocumentOf(form);
  const signatures = (issue?.signatures ?? [])
    .filter((s) => s.signerNormativeRole === 'MITIGATION_EXECUTOR' && s.mitigationItemNumber)
    .map(toSheetSignature);

  return SAFETY_MEASURE_ITEMS.map((item) => {
    const filled = form.measures.find((m) => m.itemNumber === item.number) ?? null;
    return {
      itemNumber: item.number,
      title: item.title,
      hint: item.hint,
      content: filled?.content ?? null,
      executor: filled?.executor ?? null,
      signature: signatures.find((s) => s.itemNumber === item.number) ?? null,
    };
  });
}

export interface SheetGap {
  key: string;
  text: string;
}

/**
 * Чего под нарядом НЕТ.
 *
 * Документ не должен выглядеть полным, если он не полон: инспектору важнее
 * увидеть, чего не хватает, чем красиво оформленное неполное.
 */
export function sheetGaps(form: WorkOrderForm344): SheetGap[] {
  const gaps: SheetGap[] = [];
  const issue = issueDocumentOf(form);

  if (!issue) {
    gaps.push({
      key: 'issue-absent',
      text: 'Документ выдачи не зафиксирован — под нарядом нет ни одной подписи.',
    });
  } else if (issue.signatureState !== 'signed' && issue.status !== 'SIGNED') {
    gaps.push({ key: 'issue-unsigned', text: 'Документ выдачи подписан не полностью.' });
  }

  const unsigned = measureRows(form).filter((row) => row.content !== null && row.signature === null);
  if (unsigned.length > 0) {
    gaps.push({
      key: 'measures-unsigned',
      text: `Мероприятия заполнены, но не подписаны исполнителями: ${unsigned
        .map((row) => row.itemNumber)
        .join(', ')} (пункт 7 бланка).`,
    });
  }

  // Графа 8 — внутреннее согласование (issue #445). Молчать о неполученном
  // нельзя: наряд, который требовалось согласовать и который не согласован, на
  // листе выглядит полным — а бригаду по нему допускать нельзя.
  //
  // Пробелы двух граф идут в том же порядке, что графы на листе: инспектор
  // сверяет их сверху вниз, и переставленный порядок читался бы как разговор о
  // другой графе.
  if (form.operationalApproval.required && form.operationalApproval.approvedAt === null) {
    gaps.push({
      key: 'operational-approval-missing',
      text: 'Графа 8 — согласование начальника смены (участка) не получено: допускать бригаду нельзя.',
    });
  }

  // Графа 8.1 — согласование с ВНЕШНЕЙ организацией, обслуживающей
  // коммуникации (пункт 8 Правил № 344). Подпись её представителя собирается
  // по одноразовой ссылке, без учётной записи (issue #471, карточка B-054);
  // пока она не поставлена, документ выдачи подписан не полностью. Молчать об
  // этом на листе нельзя — инспектор увидит незаполненную графу 8.1 и спросит
  // именно про неё.
  if (form.approval.required) {
    const approved = (issue?.signatures ?? []).some((s) => s.signerNormativeRole === 'SAFETY_APPROVER');
    if (!approved) {
      gaps.push({
        key: 'approval-unsigned',
        text: 'Графа 8.1 — согласование с обслуживающей организацией: подпись представителя не собрана. Её ставит представитель сторонней организации по ссылке, которую выдаёт выдающий наряд; без неё документ выдачи подписан не полностью.',
      });
    }
  }

  if (form.approval.proposed === true && form.approval.required === false) {
    const who = form.approval.waivedBy
      ? `${form.approval.waivedBy.name} (${form.approval.waivedBy.employeeNumber})`
      : 'выдающий';
    gaps.push({
      key: 'approval-waived',
      text: `Графа 8.1 — согласование по пункту 8 Правил было подставлено справочником и снято: ${who}.`,
    });
  }

  const levels = new Set((issue?.signatures ?? []).map((s) => s.signatureLevel));
  if (levels.size > 1) {
    gaps.push({
      key: 'mixed-levels',
      text: 'Подписи под документом выдачи поставлены разными способами — смотрите способ у каждой.',
    });
  }

  return gaps;
}
